import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spinner, Button, Table, Form } from 'react-bootstrap';
import { getOrders, getOrdersStatisticsTable } from '../../../services/OrdersService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Delivery Status is the single source of truth for order fulfillment state (the legacy
// Order.status field has been retired — see OrderDetail.js / TasksPage.js).
const ORDER_STATUSES = [
    { key: 'preparing', label: 'Preparing', color: '#6c757d' },
    { key: 'ready_to_deliver', label: 'Ready for delivery', color: '#0dcaf0' },
    { key: 'out_for_delivery', label: 'Out for delivery', color: '#0d6efd' },
    { key: 'delivered', label: 'Delivered', color: '#198754' },
    { key: 'cancelled', label: 'Cancelled', color: '#dc3545' },
];

export default function OrdersStatistics() {
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [filterOrderNumber, setFilterOrderNumber] = useState('');
    const [filterName, setFilterName] = useState('');
    const [filterBuyerName, setFilterBuyerName] = useState('');
    const [sortBy, setSortBy] = useState(null); // 'cost' | 'profit' | 'profit_margin' | null
    const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [oRes, tRes] = await Promise.all([
                    getOrders().catch(() => ({ data: [] })),
                    getOrdersStatisticsTable().catch(() => ({ data: [] })),
                ]);
                setOrders(oRes.data || []);
                setTableData(tRes.data || []);
            } catch {
                setOrders([]);
                setTableData([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const filteredTableData = useMemo(() => {
        let data = tableData.filter((row) => {
            const orderNum = String(row.order_number || '').toLowerCase();
            const name = String(row.name || '').toLowerCase();
            const buyer = String(row.buyer_name || '').toLowerCase();
            const fOrder = filterOrderNumber.trim().toLowerCase();
            const fName = filterName.trim().toLowerCase();
            const fBuyer = filterBuyerName.trim().toLowerCase();
            if (fOrder && !orderNum.includes(fOrder)) return false;
            if (fName && !name.includes(fName)) return false;
            if (fBuyer && !buyer.includes(fBuyer)) return false;
            return true;
        });
        if (sortBy) {
            const key = sortBy;
            const mult = sortDir === 'asc' ? 1 : -1;
            data = [...data].sort((a, b) => {
                const va = a[key] ?? 0;
                const vb = b[key] ?? 0;
                return mult * (va - vb);
            });
        }
        return data;
    }, [tableData, filterOrderNumber, filterName, filterBuyerName, sortBy, sortDir]);

    const handleSort = (col) => {
        if (sortBy === col) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortDir('desc');
        }
    };

    const SortableTh = ({ col, label }) => {
        const arrow = sortBy === col ? (sortDir === 'asc' ? '↑' : '↓') : '↓';
        return (
            <th
                role="button"
                className="user-select-none"
                onClick={() => handleSort(col)}
                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
                {label}
                <span
                    className="ms-2"
                    style={{
                        fontSize: '1.2rem',
                        fontWeight: sortBy === col ? 'bold' : 'normal',
                        color: sortBy === col ? '#0d6efd' : '#6c757d',
                        opacity: sortBy === col ? 1 : 0.7,
                    }}
                    aria-hidden
                >
                    {arrow}
                </span>
            </th>
        );
    };

    const countsByStatus = ORDER_STATUSES.reduce((acc, { key }) => {
        acc[key] = orders.filter((o) => (o.delivery_status || 'preparing') === key).length;
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" className="text-primary" />
                    <p className="mt-2 text-muted mb-0">Loading statistics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row mb-4">
                <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h4 className="heading mb-0">Orders Statistics</h4>
                    <Button variant="light" size="sm" as={Link} to="/orders">
                        <i className="fa-solid fa-arrow-left me-1" /> Back to Orders
                    </Button>
                </div>
            </div>

            <div className="row g-3">
                {ORDER_STATUSES.map(({ key, label, color }) => {
                    const count = countsByStatus[key] || 0;
                    const chartData = [{ name: label, count }];
                    return (
                        <div key={key} className="col-12 col-sm-6 col-xl-4">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0 py-2">
                                    <h6 className="mb-0">{label}</h6>
                                </Card.Header>
                                <Card.Body>
                                    <ResponsiveContainer width="100%" height={120}>
                                        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                            <Tooltip formatter={(v) => [v, 'Orders']} />
                                            <Bar dataKey="count" name="Orders" radius={[4, 4, 0, 0]}>
                                                <Cell fill={color} />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                    <div className="text-center mt-1">
                                        <span className="fw-bold fs-4">{count}</span>
                                        <span className="text-muted small ms-1">orders</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    );
                })}
            </div>

            {/* Orders Table with Filters */}
            <Card className="border-0 shadow-sm mt-4">
                <Card.Header className="bg-white border-0">
                    <h6 className="mb-0">Orders Detail</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive bordered hover className="mb-0 orders-stats-table">
                        <thead className="bg-light">
                            <tr>
                                <th>
                                    <Form.Control
                                        size="sm"
                                        placeholder="Order #"
                                        value={filterOrderNumber}
                                        onChange={(e) => setFilterOrderNumber(e.target.value)}
                                        className="border-0 bg-transparent"
                                    />
                                </th>
                                <th>
                                    <Form.Control
                                        size="sm"
                                        placeholder="Name"
                                        value={filterName}
                                        onChange={(e) => setFilterName(e.target.value)}
                                        className="border-0 bg-transparent"
                                    />
                                </th>
                                <SortableTh col="cost" label="Cost" />
                                <SortableTh col="profit" label="Profit" />
                                <SortableTh col="profit_margin" label="Margin %" />
                                <th>
                                    <Form.Control
                                        size="sm"
                                        placeholder="Buyer"
                                        value={filterBuyerName}
                                        onChange={(e) => setFilterBuyerName(e.target.value)}
                                        className="border-0 bg-transparent"
                                    />
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTableData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted py-4">
                                        {tableData.length === 0 ? 'No orders yet' : 'No matching orders'}
                                    </td>
                                </tr>
                            ) : (
                                filteredTableData.map((row) => (
                                    <tr key={row.id}>
                                        <td><strong>#{row.order_number}</strong></td>
                                        <td>{row.name}</td>
                                        <td>€{row.cost.toFixed(2)}</td>
                                        <td>€{row.profit.toFixed(2)}</td>
                                        <td>{row.profit_margin}%</td>
                                        <td>{row.buyer_name || '-'}</td>
                                        <td>
                                            <Button variant="light" size="sm" as={Link} to={`/orders/${row.id}`}>
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
}
